import {AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {Observable, ReplaySubject} from "rxjs";
import {MatPaginator, PageEvent} from "@angular/material/paginator";
import {PartnerFacility} from "../../../shared/interface/partner-facility";
import {UserPermissions} from "../../../shared/interface/user-permissions";
import {MatTableDataSource} from "@angular/material/table";
import {MatSnackBar} from "@angular/material/snack-bar";
import {MatDialog} from "@angular/material/dialog";
import {AuthService} from "../../../shared/services/auth.service";
import {FilterService} from "../../../shared/services/filter.service";
import {delay, distinctUntilChanged, takeUntil} from "rxjs/operators";
import {select, Store} from "@ngrx/store";
import {getCurrentRouteState} from "../../../shared/ngrx/selector";
import {RouterStateUrl} from "../../../shared/ngrx/route-serializer";
import {ActivatedRoute, Router} from "@angular/router";
import {NgForm} from "@angular/forms";
import {StoreRootState} from "../../../shared/ngrx/reducers";
import {CommissionSettlement} from "../../../shared/interface/commission-settlement";
import {FacilityService} from "../../../shared/services/facility.service";
import {
  PartnerFacilityVerificationSalesProcess
} from "../../../shared/interface/partner-facility-verification-sales-process";
import {CountedAndPaginated} from "../../../shared/interface/response/counted-and-paginated";
import {CommissionAnnex} from "../../../shared/interface/commission-annex";
import {animate, state, style, transition, trigger} from "@angular/animations";
import {Uuid} from "../../../shared/interface/response/uuid";
import {JobResultDialogComponent} from "../../../shared/components/job-result-dialog/job-result-dialog.component";
import {HttpErrorResponse} from "@angular/common/http";
import {ConfirmDialogComponent} from "../../../shared/components/confirm-dialog/confirm-dialog.component";
import {ContractAddDialogComponent} from "../../contracts/contract-add-dialog/contract-add-dialog.component";
import {DocumentAddDialogComponent} from "../../documents/document-add-dialog/document-add-dialog.component";
import {
  FILE_TYPE_KRS_CEIDG,
  FILE_TYPE_OVERCOMMISSION_AGREEMENT,
  FILE_TYPE_PARTNER_DATA, FILE_TYPE_SALES_PROCESS_VERIFICATION_FORM, OFWCA_STATUS_INACTIVE
} from "../../../shared/services/dict.service";
import {DocumentService} from "../../../shared/services/document.service";
import {
  FacilitiesVerificationSalesProcessAddComponent
} from "../facilities-verification-sales-process-add/facilities-verification-sales-process-add.component";
import {ListResponse} from "../../../shared/interface/response/list-response";
import {Ofwca} from "../../../shared/interface/ofwca";

@Component({
  selector: 'app-all-facilities-verification-sales-process-list',
  templateUrl: './all-facilities-verification-sales-process-list.component.html',
  styleUrls: ['./all-facilities-verification-sales-process-list.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({height: '0px', minHeight: '0'})),
      state('expanded', style({height: '*'})),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ]
})
export class AllFacilitiesVerificationSalesProcessListComponent implements OnDestroy, OnInit, AfterViewInit {
  @Input() facility: Observable<PartnerFacility>;
  @Input() facilityObject: PartnerFacility;
  @Output() changed: EventEmitter<boolean> = new EventEmitter<boolean>();
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild('filtersForm') filtersForm: NgForm;

  dataSource: PartnerFacilityVerificationSalesProcess[] = [];
  displayedColumns: string[] = ['id', 'createdAt', 'status', 'createdBy', 'download', 'upload', 'downloadAttachment', 'delete', 'actions'];
  userPermissions: ReplaySubject<UserPermissions>;
  expandedElement: PartnerFacilityVerificationSalesProcess = null;
  saveExpandedElement: PartnerFacilityVerificationSalesProcess = null;
  modify = false;
  initialFilters: any;
  totalData = 0;

  ofwcas: ReplaySubject<Ofwca[]> = new ReplaySubject<Ofwca[]>(1);
  ofwca: Ofwca[] = [];
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);


  constructor(
    private snack: MatSnackBar,
    private dialog: MatDialog,
    private auth: AuthService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private store: Store<StoreRootState>,
    private filterService: FilterService,
    private facilityService: FacilityService,
    private documentService: DocumentService,
) {
    this.userPermissions = auth.getUserPermissions();
    this.facilityService.getFacilitiesVerificationSalesProcessListSubject()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((result: CountedAndPaginated<PartnerFacilityVerificationSalesProcess>) => {
        this.dataSource = result.items;
        this.totalData = result.pagination.total;
      });
    this.parseFilters();
  }

  parseFilters(filters?: any): void {
    this.initialFilters = this.filterService.parseFiltersFromQueryParams({
      address: 'string',
    }, filters);
  }

  ngAfterViewInit(): void {
    this.paginator.page
      .pipe(
        takeUntil(this.destroyed$)
      ).subscribe((event: PageEvent) => {
      this.facilityService.loadVerificationSalesProcessList(
        event.pageIndex,
        event.pageSize,
        this.buildFilters()
      );
    });

    this.store
      .pipe(
        select(getCurrentRouteState),
        delay(0),
        distinctUntilChanged((prev: RouterStateUrl, curr: RouterStateUrl) => prev.url === curr.url),
        takeUntil(this.destroyed$),
      )
      .subscribe((state: RouterStateUrl) => {
        this.parseFilters(state.params);
        this.paginator.pageIndex = state.params.page;
        this.paginator.pageSize = state.params.pageSize;
        this.loadOfwcas();
        this.loadListData();
      })
    ;
  }

  filter(): void {
    this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
      page: 0,
      pageSize: this.paginator.pageSize,
      ...this.filtersForm.value,
    })]);
  }

  buildFilters(): any {
    const filters = {...this.initialFilters};

    this.facility.subscribe((partnerFacility: PartnerFacility) => {
      filters.address = partnerFacility.address.uuid;
    });

    return filters;
  }


  loadListData(): void {
    this.facilityService.loadVerificationSalesProcessList(this.paginator.pageIndex, this.paginator.pageSize, this.buildFilters());
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  ngOnInit(): void {
    // this.facilitie
    //   .pipe(
    //     takeUntil(this.destroyed$)
    //   ).subscribe((ofwcas: Ofwca[]) => this.ofwcasDataSource.data = ofwcas);
  }

  expandElement(partnerFacilityVerificationSalesProcess: PartnerFacilityVerificationSalesProcess): void {
    this.expandedElement = this.expandedElement === partnerFacilityVerificationSalesProcess ? null : partnerFacilityVerificationSalesProcess;
    this.modify = false;
  }

  modificationClosed(): void {
    this.saveExpandedElement = this.expandedElement;
    this.loadListData();
  }


  downloadCertificates(id: any): void {
    this.facilityService.downloadVeridicationSales(id)
      .subscribe((response: Uuid) => {
        const dialogRef = this.dialog.open(JobResultDialogComponent, {
          disableClose: true,
          width: '500px',
          data: {jobId: response.uuid, showProgress: false},
        });

        dialogRef.afterClosed().subscribe(result => {
        });
      }, (error: HttpErrorResponse) => {
        this.snack.open('Przy generowaniu dokumentów wystąpił błąd', error.status.toString(), {duration: 2000});
      });
  }

  deleteCertificates(id: any): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {text: 'Czy na pewno chcesz usunąć formularz?'},
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.facilityService.deleteVeridicationSales(id)
          .subscribe(() => {
            this.snack.open('Pomyślnie usunięto formularz', '', {duration: 2000});
            this.loadListData();
          });
      }
    });
  }

  addDocument(partnerFacilityVerificationSalesProcess: PartnerFacilityVerificationSalesProcess): void {
    const dialogRef = this.dialog.open(DocumentAddDialogComponent, {
      width: '55%',
      data: {
        facilitiesVerificationObject: partnerFacilityVerificationSalesProcess,
        types: [FILE_TYPE_SALES_PROCESS_VERIFICATION_FORM],
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      this.loadListData();
    });
  }

  getDocument(addressId: string, documentId: string): void {
      this.facilityService.downloadDocument(addressId, documentId);
  }

  addVerificationSalesProcess(): void {
    const dialogRef = this.dialog.open(FacilitiesVerificationSalesProcessAddComponent, {
      width: '70%',
      data: {partnerFacility: this.facilityObject,  ofwcas: this.ofwca},
      height: '90%',
      maxHeight: '90%',
      panelClass: 'full-height-dialog',
    });

    dialogRef.afterClosed().subscribe(result => {
      this.loadListData();
    });
  }

  loadOfwcas(): void {
    const val = [];
    this.facilityService.getFacilityOfwcas(this.facilityObject.address.uuid )
      .subscribe((ofwcas: ListResponse<Ofwca>) => {
        this.ofwcas.next(ofwcas.items);
        this.ofwca = ofwcas.items;

      this.ofwca.forEach((ofwca: Ofwca) => {
        if (ofwca.status.key !== OFWCA_STATUS_INACTIVE){
          val.push(ofwca);
        }
      });
      this.ofwca = val;
  });

}

}

